import { type FunctionComponent, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { useNewsAgendaContext } from "scmp-app/components/plus/news-agenda/contexts";
import type { helpersGetArticleTypeArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleTypeArticle.graphql";
import type { rightPlusNewsAgendaRightQuery } from "scmp-app/queries/__generated__/rightPlusNewsAgendaRightQuery.graphql";

import { PlusNewsAgendaRightContent } from "./content";
import { PlusNewsAgendaRightHead } from "./head";
import { getParsedHtml, getTypeValue } from "./helpers";
import NoEntries from "./no-entries.svg";
import {
  Container,
  Content,
  Contents,
  ContentsWrapper,
  Description,
  DescriptionWrapper,
  Head,
  Headline,
} from "./styles";

export const PlusNewsAgendaRight: FunctionComponent = () => {
  const { currentAgendaId, showEmptyAgenda } = useNewsAgendaContext();

  const queryReference = useLazyLoadQuery<rightPlusNewsAgendaRightQuery>(
    graphql`
      query rightPlusNewsAgendaRightQuery($entityId: String) {
        topic(filter: { entityId: $entityId }) {
          ...headPlusNewsAgendaRightHeadTopic
          description {
            html
          }
          contents(first: 10, orderBy: { field: PUBLISHED_DATE, direction: DESC }) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...helpersGetArticleTypeArticle
                  ...contentPlusNewsAgendaRightContentContent
                }
              }
            }
          }
        }
      }
    `,
    {
      entityId: currentAgendaId,
    },
    { fetchPolicy: "store-or-network" },
  );

  const sortedEdges = useMemo(
    () =>
      [...(queryReference.topic.contents?.edges ?? [])].toSorted(
        (
          a: { node: helpersGetArticleTypeArticle$key },
          b: { node: helpersGetArticleTypeArticle$key },
        ) => {
          const aTypeId = getArticleType(a.node) ?? "";
          const bTypeId = getArticleType(b.node) ?? "";
          return getTypeValue(aTypeId) - getTypeValue(bTypeId);
        },
      ),
    [queryReference.topic.contents?.edges],
  );

  const isNotHaveRelatedContent = useMemo(() => sortedEdges.length === 0, [sortedEdges.length]);

  const updatedHtml = getParsedHtml(queryReference.topic?.description?.html ?? "");

  if (!currentAgendaId) {
    if (showEmptyAgenda) {
      return <NoEntries />;
    }
    return null;
  }

  return (
    <Container>
      <Head>
        <PlusNewsAgendaRightHead reference={queryReference.topic} />
      </Head>
      <Contents>
        <DescriptionWrapper $isFullSize={isNotHaveRelatedContent}>
          <Headline>EVENT DESCRIPTION</Headline>
          {updatedHtml && <Description dangerouslySetInnerHTML={{ __html: updatedHtml }} />}
        </DescriptionWrapper>
        {!isNotHaveRelatedContent && (
          <ContentsWrapper>
            <Headline>RELATED CONTENT</Headline>
            {sortedEdges.map(edge => {
              if (edge.node) {
                return (
                  <Content key={edge.node.entityId}>
                    <PlusNewsAgendaRightContent reference={edge.node} />
                  </Content>
                );
              }
            })}
          </ContentsWrapper>
        )}
      </Contents>
    </Container>
  );
};

PlusNewsAgendaRight.displayName = "PlusNewsAgendaRight";
